import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { locale, t, n } from '@/lib/i18n';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Dates in the reader's language.
 *
 * `n()` is applied on top of Intl rather than trusted to it: the Nepali locale
 * is requested but not installed on every device, and a browser that falls back
 * to en-US would otherwise print Latin digits inside an otherwise Nepali page.
 * In English `n()` is a no-op, so the same call is correct either way.
 */
export function formatDate(iso: string | null | undefined, opts: { withYear?: boolean } = {}): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const formatted = new Intl.DateTimeFormat(locale(), {
    day: 'numeric',
    month: 'long',
    ...(opts.withYear === false ? {} : { year: 'numeric' }),
  }).format(date);

  return n(formatted);
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.round((then - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ];

  const formatter = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return n(formatter.format(Math.round(seconds / size), unit));
  }
  return t('भर्खरै');
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => [...p][0] ?? '').join('') || '✍';
}
